import { Box, Button, Divider, Drawer, Link, NavLink, styled } from '@medely/web-components';
import { useFirebaseContext, useConfigurationContext } from '@medely/ui-kit';
import {
  CalendarCheck,
  CalendarDay,
  CalendarLines,
  CircleDollar,
  CircleQuestion,
  CircleUser,
  ClipboardCheck,
  Clock,
  Gear,
  IdCard,
  Menu,
  RightFromBracket,
  Search,
  Share,
} from '@medely/web-components/icons';
import { assignmentOnboardingRoute, mobileSettingsRoutes, useRoutes, ONBOARDING } from 'routes';
import useDialog from 'hooks/useDialog';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import useOnboarding from 'hooks/useOnboarding';
import { gateNames, useGate, useCurrentUser, isSaasAccount, isAgencyAccount } from '@mp';
import { ReferralContainer } from '@src/components/referrals/ReferralContainer';

const ICONS = {
  Onboarding: ClipboardCheck,
  Today: CalendarCheck,
  'Find Shifts': Search,
  'My Shifts': CalendarDay,
  Timesheets: Clock,
  Earnings: CircleDollar,
  Credentials: IdCard,
  'My Assignments': CalendarLines,
  Profile: CircleUser,
  Settings: Gear,
};

const TopBarMobileNavigationLink = styled(NavLink)({ fontVariant: 'body1', py: 2, px: 0 });
const NavAction = styled(Link)(({ theme }) => ({
  all: 'initial',
  display: 'block',
  cursor: 'pointer',
  textDecoration: 'none',
  // @ts-ignore
  fontFamily: theme.typography.fontFamily,
  padding: '12px',
  // @ts-ignore
  fontSize: theme.typography.fontSize,
  lineHeight: '24px',
  color: theme.palette.grey[900],
  fontVariant: 'body1',
}));

const TopBarMobileNavigation = (): React.ReactElement => {
  const { help } = useConfigurationContext();
  const dialog = useDialog();
  const navigate = useNavigate();
  const { assignmentsMissingConditions, isAssignmentOnboardingRequired, showOnboarding } =
    useOnboarding();
  const { currentUser, isApplicant, isDeactivated, isMarketplace } = useCurrentUser();
  const { mobile: mobileRoutes } = useRoutes();
  const isSaasProfessional = isSaasAccount(currentUser);
  const isAgencyProfessional = isAgencyAccount(currentUser);
  const isMarketplaceApplicant = isApplicant && !isSaasProfessional && !isAgencyProfessional;
  const { logout } = useFirebaseContext();
  const referralLinkGate = useGate(gateNames.referralLinkGate);
  const showReferralLink = referralLinkGate && !isDeactivated && isMarketplace;

  const handleNavigation = (path: string) => {
    navigate(path);
    dialog.closeDialog();
  };
  const handleLogout = () => {
    dialog.closeDialog();
    logout?.();
  };

  const RouteButton = ({ route }): React.ReactElement => {
    const Icon = ICONS[route.name as keyof typeof ICONS];

    return (
      <Box display="flex" alignItems="center">
        <Icon />
        <TopBarMobileNavigationLink
          key={route.name}
          to={route.path}
          end={route.exact}
          onClick={() => handleNavigation(route.path)}
        >
          {route.name}
        </TopBarMobileNavigationLink>
      </Box>
    );
  };

  return (
    <>
      <Button sx={{ mr: 1 }} onClick={dialog.openDialog}>
        <Menu />
      </Button>
      <Drawer open={dialog.open} onClose={dialog.closeDialog}>
        <Box py={2} px={3} fontWeight={600}>
          Menu
        </Box>
        <Box sx={{ width: 320 }}>
          {!isMarketplaceApplicant && (
            <>
              {(showOnboarding || isAssignmentOnboardingRequired) && !isDeactivated && (
                <Box px={3}>
                  <RouteButton
                    route={
                      isAssignmentOnboardingRequired
                        ? assignmentOnboardingRoute(assignmentsMissingConditions[0])
                        : ONBOARDING
                    }
                    key="onboarding"
                  />
                </Box>
              )}

              <Box px={3}>
                {mobileRoutes.map((route) => (
                  <RouteButton route={route} key={route.name} />
                ))}
              </Box>
              <Box px={3}>
                {mobileSettingsRoutes.map((route) => (
                  <RouteButton route={route} key={route.name} />
                ))}
              </Box>
              <Divider />
            </>
          )}
          <Box py={2} px={3}>
            {showReferralLink && (
              <Box position="relative" display="flex" alignItems="center">
                <Share />
                <NavAction>
                  <ReferralContainer isPopup />
                </NavAction>
              </Box>
            )}
            <Box display="flex" alignItems="center">
              <CircleQuestion />
              <NavAction external href={help.url}>
                Help
              </NavAction>
            </Box>
            <Box display="flex" alignItems="center">
              <RightFromBracket />
              <NavAction onClick={handleLogout}>Log out</NavAction>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default TopBarMobileNavigation;
